import { useEffect, useMemo, useState } from "react";

import { type FavoriteView, type LoggedMeal, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { type AppCopy, appCopy } from "../lib/i18n/app";
import { ScreenShell } from "./screen-shell";
import { IconSearch } from "./ui/icon";
import { H1, MonoMeta, TopBar } from "./ui/primitives";

type Props = {
  householdId: string;
  onLogged: (meal: LoggedMeal) => void;
  onBack: () => void;
};

type SortMode = "useCount" | "lastUsed" | "alphabetical";

const SORT_MODES: SortMode[] = ["useCount", "lastUsed", "alphabetical"];

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

function compareFavorites(a: FavoriteView, b: FavoriteView, mode: SortMode): number {
  if (mode === "useCount") {
    if (b.useCount !== a.useCount) return b.useCount - a.useCount;
    return (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0);
  }
  if (mode === "lastUsed") {
    return (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0);
  }
  return a.name.localeCompare(b.name, "pl");
}

function relativeLast(ts: number | null, t: AppCopy): string {
  if (!ts) return t.favRelNever;
  const days = Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000));
  if (days <= 0) return t.favRelToday;
  if (days === 1) return t.favRelYesterday;
  if (days < 7) return t.favRelDays(days);
  if (days < 14) return t.favRelWeekAgo;
  if (days < 30) return t.favRelWeeks(Math.floor(days / 7));
  return t.favRelMonths(Math.floor(days / 30));
}

export function FavoritesScreen({ householdId, onLogged, onBack }: Props) {
  const { lang } = useLang();
  const t = appCopy[lang];
  const SORT_LABELS: Record<SortMode, string> = {
    useCount: t.favSortUseCount,
    lastUsed: t.favSortLastUsed,
    alphabetical: t.favSortAlphabetical,
  };
  const [favorites, setFavorites] = useState<FavoriteView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>("useCount");
  const [loggingId, setLoggingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listFavorites(householdId)
      .then((favs) => {
        if (!cancelled) setFavorites(favs);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t.favLoadFailed);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, t.favLoadFailed]);

  const filtered = useMemo(() => {
    if (!favorites) return [];
    const q = normalize(query.trim());
    const list = q ? favorites.filter((f) => normalize(f.name).includes(q)) : favorites;
    return [...list].sort((a, b) => compareFavorites(a, b, sort));
  }, [favorites, query, sort]);

  async function handleLog(fav: FavoriteView) {
    setLoggingId(fav.id);
    setError(null);
    try {
      const meal = await api.logFavorite(householdId, fav.id);
      if (meal) onLogged(meal);
      setFavorites((prev) =>
        prev
          ? prev.map((f) =>
              f.id === fav.id ? { ...f, useCount: f.useCount + 1, lastUsedAt: Date.now() } : f,
            )
          : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t.favLogFailed);
    } finally {
      setLoggingId(null);
    }
  }

  async function handleDelete(fav: FavoriteView) {
    if (!confirm(t.favDeleteConfirm(fav.name))) return;
    setDeletingId(fav.id);
    setError(null);
    try {
      await api.deleteFavorite(householdId, fav.id);
      setFavorites((prev) => (prev ? prev.filter((f) => f.id !== fav.id) : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : t.favDeleteFailed);
    } finally {
      setDeletingId(null);
    }
  }

  const count = favorites?.length ?? 0;

  return (
    <ScreenShell hideHeader withBottomNav>
      <div style={{ marginBottom: 18 }}>
        <TopBar
          back={t.favBack}
          title={t.favTopTitle}
          onBack={onBack}
          right={
            favorites && favorites.length > 0 ? (
              <button
                type="button"
                onClick={() => setEditMode((v) => !v)}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--accent)",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {editMode ? t.favDone : t.favEdit}
              </button>
            ) : undefined
          }
        />
      </div>

      <H1 accent=".">{t.favHeading}</H1>
      {favorites !== null ? (
        <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>
          <span
            className="tabular"
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 800,
              color: "var(--ink)",
            }}
          >
            {t.favCount(count)}
          </span>{" "}
          · {t.favTagline}
        </p>
      ) : (
        <p style={{ marginTop: 12, fontSize: 14, color: "var(--muted)" }}>{t.favLoading}</p>
      )}

      {/* Search */}
      <div
        style={{
          marginTop: 20,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 14px",
          background: "var(--surface)",
          borderRadius: 10,
          color: "var(--muted)",
          boxShadow: "0 0 0 1px var(--nav-border)",
        }}
      >
        <IconSearch size={16} />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.favSearchPlaceholder}
          className="flex-1 outline-none bg-transparent"
          style={{
            // 16px iOS auto-zoom guard — see primitives.tsx Input.
            fontSize: 16,
            color: "var(--ink)",
            border: "none",
          }}
        />
      </div>

      {/* Sort segmented control */}
      <div
        style={{
          marginTop: 12,
          display: "flex",
          gap: 0,
          borderRadius: 8,
          overflow: "hidden",
          background: "var(--surface)",
          boxShadow: "0 0 0 1px var(--nav-border)",
          padding: 3,
        }}
      >
        {SORT_MODES.map((k) => {
          const active = sort === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setSort(k)}
              className="flex-1 text-center transition-colors"
              style={{
                padding: "8px 10px",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.06em",
                borderRadius: 6,
                background: active ? "var(--ink)" : "transparent",
                color: active ? "var(--bg)" : "var(--ink-2)",
                border: "none",
                cursor: "pointer",
              }}
            >
              {SORT_LABELS[k]}
            </button>
          );
        })}
      </div>

      {error ? (
        <p
          className="text-[12px] px-3 py-2 mt-3 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
        >
          {error}
        </p>
      ) : null}

      {favorites !== null && favorites.length === 0 ? (
        <p
          className="text-center px-4"
          style={{ marginTop: 24, fontSize: 13, color: "var(--muted)" }}
        >
          {t.favEmptyState}
        </p>
      ) : null}

      {favorites !== null && favorites.length > 0 && filtered.length === 0 ? (
        <p
          className="text-center px-4"
          style={{ marginTop: 24, fontSize: 13, color: "var(--muted)" }}
        >
          {t.favNoMatch(query)}
        </p>
      ) : null}

      {/* List */}
      <div style={{ marginTop: 16 }}>
        {filtered.map((fav) => (
          <div
            key={fav.id}
            style={{
              padding: "14px 0",
              display: "flex",
              alignItems: "center",
              gap: 14,
              borderBottom: "1px solid var(--nav-border)",
            }}
          >
            <div style={{ flex: "0 0 36px", textAlign: "right" }}>
              <div
                className="tabular"
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 20,
                  fontWeight: 800,
                  color: "var(--accent)",
                  letterSpacing: "-0.03em",
                }}
              >
                {fav.useCount}
                <span style={{ fontSize: 12, color: "var(--muted)" }}>×</span>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--ink)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fav.name}
              </div>
              <MonoMeta>
                {fav.kcal} KCAL
                {fav.proteinG !== null ? ` · B ${Math.round(fav.proteinG)}` : ""}
                {fav.fatG !== null ? ` · T ${Math.round(fav.fatG)}` : ""}
                {fav.carbsG !== null ? ` · W ${Math.round(fav.carbsG)}` : ""}
                {" · "}
                {relativeLast(fav.lastUsedAt, t)}
              </MonoMeta>
            </div>
            {editMode ? (
              <button
                type="button"
                onClick={() => handleDelete(fav)}
                disabled={deletingId === fav.id}
                className="uppercase disabled:opacity-50"
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: "transparent",
                  color: "var(--bad-fg)",
                  boxShadow: "inset 0 0 0 1px var(--bad-fg)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                }}
                aria-label={t.favDeleteAria(fav.name)}
              >
                {deletingId === fav.id ? "…" : t.favDelete}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleLog(fav)}
                disabled={loggingId === fav.id}
                className="uppercase disabled:opacity-50"
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: "var(--ink)",
                  color: "var(--bg)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                }}
              >
                {loggingId === fav.id ? "…" : t.favLog}
              </button>
            )}
          </div>
        ))}
      </div>
    </ScreenShell>
  );
}
